import React, { useState, useRef, useEffect } from "react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { addressLine1Validation, cityValidation, countryValidation, firstNameValidation, lastNameValidation, stateValidation, zipcodeValidation } from "validationSchemas/common";

const libraries: Array<"places"> = ["places"];
import { getAuthCookie } from "../../utils/authCookies";
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SignageTeammemberAddAddress = () => {
  const [preview, setPreview] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0); // Track upload progress
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const token = getAuthCookie("sessionToken");
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const formik = useFormik({
    initialValues: {
      label_of_address: "",
      firstName: "",
      lastName: "",
      company: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      county: "",
      city: "",
      state: "",
      zipcode: "",
      phone: "",
      cityCode: "",
      stateCode: "",
      countryCode: "",
      latitude: 0,
      longitude: 0,
    },
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      addressLine1: addressLine1Validation,
      country: countryValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      label_of_address: Yup.string().required("Label of address is required"),
      city: cityValidation,
    }),
    onSubmit: async (values) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              user_id: userId,
              label: values.label_of_address,
              first_name: values.firstName,
              last_name: values.lastName,
              company: values.company,
              address_details: {
                label: values.label_of_address,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                zip: values.zipcode,
                country: values.country,
                phone: values.phone,
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: values.countryCode,
                latitude: values.latitude,
                longitude: values.longitude, 
              },
            }),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to create brand");
        }

        toast.success("Address added successfully!");
        navigate("/signageteammember/addresses", {
          state: { successMessage: "Address added successfully!" },
        });
      } catch (error: any) {
        console.error(error);
        toast.error(`Error: ${error.message}`);
      } finally {
        setFormSubmitting(false);
        setUploadProgress(0);
      }
    },
  });

  // Handle file selection and integrate with Formik
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      formik.setFieldValue("image", file); // Save the file object in Formik
      setPreview(URL.createObjectURL(file)); // Set preview for display
    }
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      console.log("Place changed:", place);
  
      const addressComponents = place.address_components;
      console.log(addressComponents);
  
      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };
  
        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;
  
        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');

  
        const street = `${streetNumber} ${route}`.trim();
  
        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();
  
        console.log("Latitude:", latitude, "Longitude:", longitude);
  
        // Update form values
        formik.setFieldValue('streetAddress1', street);
        formik.setFieldValue('city', cityObj.long_name);
        formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        formik.setFieldValue('state', stateObj.long_name);
        formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        formik.setFieldValue('zipcode', postalCode);
        formik.setFieldValue('country', countryObj.long_name);
        formik.setFieldValue('countryCode', countryObj.short_name); 
  
        if (latitude && longitude) {
          formik.setFieldValue('latitude', latitude);
          formik.setFieldValue('longitude', longitude);
        }
      }
    }
  };

  return (
    <div className="content">
      <div className="card">
        <div className="card-body">
          <h2>Add Address</h2>

          <div className="row justify-content-center">
            <div className="col-md-12 col-lg-12 col-xl-12">
              <div className="card mt-4 card-bg-fill">
                <div className="card-body p-4">
                  <div className="p-2 mt-4">
                    <form onSubmit={formik.handleSubmit}>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Label of address
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="label_of_address"
                                name="label_of_address"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.label_of_address}
                              />
                              {formik.touched.label_of_address &&
                              formik.errors.label_of_address ? (
                                <div className="text-danger">
                                  {formik.errors.label_of_address}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                First Name
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="firstName"
                                name="firstName"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.firstName}
                              />
                              {formik.touched.firstName &&
                              formik.errors.firstName ? (
                                <div className="text-danger">
                                  {formik.errors.firstName}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Last Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="lastName"
                                name="lastName"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.lastName}
                              />
                              {formik.touched.lastName &&
                              formik.errors.lastName ? (
                                <div className="text-danger">
                                  {formik.errors.lastName}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">Company</label>
                              <input
                                type="text"
                                className="form-control"
                                id="company"
                                name="company"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.company}
                              />
                              {formik.touched.company &&
                              formik.errors.company ? (
                                <div className="text-danger">
                                  {formik.errors.company}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Address Line 1
                                <span className="text-danger">*</span>
                              </label>
                              {isLoaded && (
                                <Autocomplete
                                  onLoad={(autocomplete) => {
                                    autocompleteRef.current = autocomplete;
                                  }}
                                  onPlaceChanged={handlePlaceChanged}
                                >
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="addressLine1"
                                    name="addressLine1"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.addressLine1}
                                  />
                                </Autocomplete>
                              )}
                              {formik.touched.addressLine1 &&
                              formik.errors.addressLine1 ? (
                                <div className="text-danger">
                                  {formik.errors.addressLine1}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Address Line 2
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="addressLine2"
                                name="addressLine2"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.addressLine2}
                              />
                              {formik.touched.addressLine2 &&
                              formik.errors.addressLine2 ? (
                                <div className="text-danger">
                                  {formik.errors.addressLine2}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        
                        {/* <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">County</label>
                              <input
                                type="text"
                                className="form-control"
                                id="county"
                                name="county"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.county}
                              />
                              {formik.touched.county && formik.errors.county ? (
                                <div className="text-danger">
                                  {formik.errors.county}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div> */}
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                City <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="city"
                                name="city"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.city}
                              />
                              {formik.touched.city && formik.errors.city ? (
                                <div className="text-danger">
                                  {formik.errors.city}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                State <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="state"
                                name="state"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.state}
                              />
                              {formik.touched.state && formik.errors.state ? (
                                <div className="text-danger">
                                  {formik.errors.state}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Zip Code <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="name"
                                name="name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.zipcode}
                              />
                              {formik.touched.zipcode &&
                              formik.errors.zipcode ? (
                                <div className="text-danger">
                                  {formik.errors.zipcode}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Country <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="country"
                                name="country"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.country}
                              />
                              {formik.touched.country &&
                              formik.errors.country ? (
                                <div className="text-danger">
                                  {formik.errors.country}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">Phone number</label>
                              <input
                                type="text"
                                className="form-control"
                                id="phone"
                                name="phone"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.phone}
                              />
                              {formik.touched.phone && formik.errors.phone ? (
                                <div className="text-danger">
                                  {formik.errors.phone}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="mt-4">
                        <button
                          className="btn btn-success w-100"
                          type="submit"
                          disabled={formSubmitting}
                        >
                          {formSubmitting ? "Submitting..." : "Submit"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageTeammemberAddAddress;
